import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Tag,
  Typography,
  message,
  Tooltip,
  Popconfirm,
  Modal,
  Descriptions,
  Divider,
  Spin,
} from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  SwapOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Api from '../../Network';
import MobileTable from '../../Component/MobileTable';
import { usePermissions } from '../../contexts/PermissionContext';
import type { Visit } from '../../types/types';

const { Text } = Typography;

interface VisitRequest {
  id: number;
  visit_id: number;
  trade_name: string;
  business_name: string;
  customer_name: string;
  customer_phone: string;
  status: string;
  current_sales_person: {
    id: number;
    name: string;
    phone_number: string;
  } | null;
  requested_sales_person: {
    id: number;
    name: string;
    phone_number: string;
  } | null;
  requested_at: string;
  approved_by: {
    id: number;
    name: string;
  } | null;
}

interface FollowUpRequestsProps {
  onRefresh?: () => void;
}

const FollowUpRequests: React.FC<FollowUpRequestsProps> = ({ onRefresh }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [visitRequests, setVisitRequests] = useState<VisitRequest[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [selectedRequest, setSelectedRequest] = useState<VisitRequest | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [visitDetail, setVisitDetail] = useState<Visit | null>(null);
  const [visitDetailLoading, setVisitDetailLoading] = useState(false);

  const fetchVisitRequests = async (page = 1, pageSize = 25) => {
    setLoading(true);
    try {
      const response: any = await Api.get(
        `/api/admin/visit-requests?page=${page}&per-page=${pageSize}`
      );

      if (response.status && response.data) {
        setVisitRequests(response.data.data || []);
        setPagination({
          current: response.data.current_page || page,
          pageSize: response.data.per_page || pageSize,
          total: response.data.total || 0,
        });
      } else {
        message.error(response.message || 'Failed to fetch visit requests');
      }
    } catch (error) {
      message.error('Failed to fetch visit requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitRequests();
  }, []);

  const handleAction = async (requestId: number, action: 'approved' | 'rejected') => {
    setActionLoading(true);
    try {
      const response: any = await Api.post('/api/admin/visit-requests', {
        visit_request_id: requestId,
        status: action,
      });

      if (response.status) {
        message.success(
          t(`visits.followUpRequests.${action}Success`, `Request ${action} successfully`)
        );
        fetchVisitRequests(pagination.current, pagination.pageSize);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        message.error(response.message || `Failed to ${action} request`);
      }
    } catch (error) {
      message.error(`Failed to ${action} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchVisitDetail = async (visitId: number) => {
    setVisitDetailLoading(true);
    setVisitDetail(null);
    try {
      const response = await Api.get<{ status?: boolean; data?: Visit; message?: string }>(
        `/api/admin/visits/${visitId}`
      );
      if (response.status && response.data) {
        setVisitDetail(response.data);
      } else {
        message.error(
          response.message || t('visits.followUpRequests.visitLoadError', 'Failed to load visit')
        );
      }
    } catch {
      message.error(t('visits.followUpRequests.visitLoadError', 'Failed to load visit'));
    } finally {
      setVisitDetailLoading(false);
    }
  };

  const handleViewDetails = (record: VisitRequest) => {
    setSelectedRequest(record);
    setDetailsVisible(true);
    void fetchVisitDetail(record.visit_id);
  };

  const closeDetailsModal = () => {
    setDetailsVisible(false);
    setSelectedRequest(null);
    setVisitDetail(null);
  };

  const getVisitStatusText = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'pending') return t('visits.status.pending');
    if (s === 'completed') return t('visits.status.completed');
    if (s === 'cancelled' || s === 'canceled') return t('visits.status.cancelled');
    return status;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'orange';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns: any[] = [
    {
      title: t('visits.followUpRequests.columns.id', 'ID'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      'data-label': t('visits.followUpRequests.columns.id', 'ID'),
    },
    {
      title: t('visits.followUpRequests.columns.tradeName', 'Trade Name'),
      dataIndex: 'trade_name',
      key: 'trade_name',
      'data-label': t('visits.followUpRequests.columns.tradeName', 'Trade Name'),
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: t('visits.followUpRequests.columns.currentSalesPerson', 'Current Sales Person'),
      dataIndex: 'current_sales_person',
      key: 'current_sales_person',
      'data-label': t('visits.followUpRequests.columns.currentSalesPerson', 'Current Sales Person'),
      render: (person: any) => (
        <Space direction="vertical" size="small">
          <Space>
            <UserOutlined />
            <Text>{person?.name || t('common.notAssigned', 'Not Assigned')}</Text>
          </Space>
          {person?.phone_number && (
            <Space>
              <PhoneOutlined />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {person.phone_number}
              </Text>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: t('visits.followUpRequests.columns.requestedSalesPerson', 'Requested Sales Person'),
      dataIndex: 'requested_sales_person',
      key: 'requested_sales_person',
      'data-label': t(
        'visits.followUpRequests.columns.requestedSalesPerson',
        'Requested Sales Person'
      ),
      render: (person: any) => (
        <Space direction="vertical" size="small">
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <Text style={{ color: '#1890ff', fontWeight: 500 }}>
              {person?.name || t('common.unknown', 'Unknown')}
            </Text>
          </Space>
          {person?.phone_number && (
            <Space>
              <PhoneOutlined />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {person.phone_number}
              </Text>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: t('visits.followUpRequests.columns.requestedAt', 'Requested At'),
      dataIndex: 'requested_at',
      key: 'requested_at',
      'data-label': t('visits.followUpRequests.columns.requestedAt', 'Requested At'),
      render: (text: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{new Date(text).toLocaleString()}</Text>
        </Space>
      ),
    },
    {
      title: t('visits.followUpRequests.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      'data-label': t('visits.followUpRequests.columns.status', 'Status'),
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`visits.status.${status}`, status.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: t('visits.followUpRequests.columns.actions', 'Actions'),
      key: 'actions',
      fixed: 'right',
      width: 200,
      'data-label': t('visits.followUpRequests.columns.actions', 'Actions'),
      render: (_: any, record: VisitRequest) => {
        const canApprove =
          hasPermission('approve_visit') ||
          hasPermission('update_visit') ||
          hasPermission('visits.approve');
        const canReject =
          hasPermission('reject_visit') ||
          hasPermission('update_visit') ||
          hasPermission('visits.reject');

        return (
          <Space size="small">
            <Tooltip title={t('common.view', 'View')}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewDetails(record)}
              />
            </Tooltip>
            {record.status === 'pending' && (
              <>
                {canApprove && (
                  <Popconfirm
                    title={t(
                      'visits.followUpRequests.confirmApprove',
                      'Are you sure you want to approve this request?'
                    )}
                    onConfirm={() => handleAction(record.id, 'approved')}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Tooltip title={t('visits.actions.approve', 'Approve')}>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        size="small"
                        loading={actionLoading}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
                {canReject && (
                  <Popconfirm
                    title={t(
                      'visits.followUpRequests.confirmReject',
                      'Are you sure you want to reject this request?'
                    )}
                    onConfirm={() => handleAction(record.id, 'rejected')}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Tooltip title={t('visits.actions.reject', 'Reject')}>
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        size="small"
                        loading={actionLoading}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (newPagination: any) => {
    fetchVisitRequests(newPagination.current, newPagination.pageSize);
  };

  return (
    <>
      <MobileTable
        columns={columns}
        dataSource={visitRequests}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total: number, range: [number, number]) =>
            `${t('common.showing')} ${range[0]}-${range[1]} ${t('common.of')} ${total} ${t('visits.items')}`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />

      {/* Details Modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            {t('visits.followUpRequests.detailsTitle', 'Follow-up Request Details')}
          </Space>
        }
        open={detailsVisible}
        onCancel={closeDetailsModal}
        footer={[
          <Button key="close" onClick={closeDetailsModal}>
            {t('common.close', 'Close')}
          </Button>,
        ]}
        width={900}
      >
        {selectedRequest && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('visits.followUpRequests.columns.id', 'Request ID')}>
                {selectedRequest.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.followUpRequests.columns.visitId', 'Visit ID')}>
                {selectedRequest.visit_id}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('visits.followUpRequests.columns.tradeName', 'Trade Name')}
              >
                <Text strong>{selectedRequest.trade_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={t('visits.followUpRequests.columns.businessName', 'Business Name')}
              >
                {selectedRequest.business_name}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('visits.followUpRequests.columns.customerName', 'Customer Name')}
              >
                {selectedRequest.customer_name}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('visits.followUpRequests.columns.customerPhone', 'Customer Phone')}
              >
                <Space>
                  <PhoneOutlined />
                  <Text copyable>{selectedRequest.customer_phone}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  'visits.followUpRequests.columns.currentSalesPerson',
                  'Current Sales Person'
                )}
              >
                {selectedRequest.current_sales_person ? (
                  <Space direction="vertical" size="small">
                    <Text>{selectedRequest.current_sales_person.name}</Text>
                    <Text type="secondary">
                      {selectedRequest.current_sales_person.phone_number}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">{t('common.notAssigned', 'Not Assigned')}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  'visits.followUpRequests.columns.requestedSalesPerson',
                  'Requested Sales Person'
                )}
              >
                {selectedRequest.requested_sales_person ? (
                  <Space direction="vertical" size="small">
                    <Text strong style={{ color: '#1890ff' }}>
                      {selectedRequest.requested_sales_person.name}
                    </Text>
                    <Text type="secondary">
                      {selectedRequest.requested_sales_person.phone_number}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">{t('common.unknown', 'Unknown')}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('visits.followUpRequests.columns.requestedAt', 'Requested At')}
              >
                {new Date(selectedRequest.requested_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('visits.followUpRequests.columns.status', 'Status')}>
                <Tag color={getStatusColor(selectedRequest.status)}>
                  {t(
                    `visits.status.${selectedRequest.status}`,
                    selectedRequest.status.toUpperCase()
                  )}
                </Tag>
              </Descriptions.Item>
              {selectedRequest.approved_by && (
                <Descriptions.Item
                  label={t('visits.followUpRequests.columns.approvedBy', 'Approved By')}
                >
                  {selectedRequest.approved_by.name}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">
              {t('visits.followUpRequests.linkedVisitTitle', 'Visit details')}
            </Divider>

            {visitDetailLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin size="large" />
              </div>
            ) : visitDetail ? (
              <>
                <Descriptions
                  bordered
                  column={1}
                  title={t('visits.details.basicInfo', 'Basic information')}
                >
                  <Descriptions.Item label={t('visits.columns.id')}>
                    {visitDetail.id}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.status')}>
                    <Tag color={getStatusColor(visitDetail.status)}>
                      {getVisitStatusText(visitDetail.status)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.businessName')}>
                    {visitDetail.business_name}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.tradeName')}>
                    <Text strong>{visitDetail.trade_name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.customerName')}>
                    {visitDetail.customer_name}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.customerPhone')}>
                    <Space>
                      <PhoneOutlined />
                      <Text copyable>{visitDetail.customer_phone}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.location')}>
                    <Space>
                      <EnvironmentOutlined />
                      <Text>{visitDetail.customer_location}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.driver')}>
                    <Space>
                      <UserOutlined />
                      <Text>{visitDetail.driver?.name ?? '—'}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('visits.columns.createdAt')}>
                    <Space>
                      <CalendarOutlined />
                      <Text>{new Date(visitDetail.created_at).toLocaleString()}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('common.updatedAt', 'Updated at')}>
                    <Text>{new Date(visitDetail.updated_at).toLocaleString()}</Text>
                  </Descriptions.Item>
                </Descriptions>

                {visitDetail.follow_ups && visitDetail.follow_ups.length > 0 && (
                  <>
                    <Divider orientation="left">
                      <Space>
                        <FileTextOutlined />
                        {t('visits.details.followUps', 'Follow-ups')}
                      </Space>
                    </Divider>
                    {visitDetail.follow_ups.map((fu) => (
                      <Descriptions
                        key={fu.id}
                        bordered
                        column={1}
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={`#${fu.id} — ${fu.driver?.name ?? ''}`}
                      >
                        <Descriptions.Item label={t('visits.details.communicationWay')}>
                          {fu.communication_way}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('visits.details.report')}>
                          {fu.report || '—'}
                        </Descriptions.Item>
                        {fu.next_visit_date && (
                          <Descriptions.Item
                            label={t('visits.followUpRequests.nextVisitDate', 'Next visit date')}
                          >
                            {new Date(fu.next_visit_date).toLocaleString()}
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('visits.details.createdAt')}>
                          {new Date(fu.created_at).toLocaleString()}
                        </Descriptions.Item>
                      </Descriptions>
                    ))}
                  </>
                )}
              </>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
};

export default FollowUpRequests;
